import React, { useEffect, useState, ChangeEvent } from "react";
import ExpansionPanel from "@material-ui/core/ExpansionPanel";
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { makeStyles, createStyles } from "@material-ui/styles";
import {
  Theme,
  Grid,
  Typography,
  InputAdornment,
  Card,
  CardContent,
} from "@material-ui/core";
import * as Yup from "yup";
import clsx from "clsx";
import useSnackbar from "../../../hooks/snackbar/useSnackbar";
import useChangeTracker from "../../../hooks/change-tracker/useChangeTracker";
import ValidationConfiguration from "../../../hooks/change-tracker/ValidationConfiguration";
import TrackerName from "../../../models/payment-request/PaymentRequestTrackerName";
import useInput from "../../../hooks/input/useInput";
import TrTextField from "../../shared/controls/TrTextField";
import TrSnackbar from "../../shared/controls/TrSnackbar";
import StepHandler from "../../../models/shared/StepHandler";
import PaymentRequestAuthorizedCarrier from "../../../models/payment-request/PaymentRequestAuthorizedCarrier";
import PaymentRequestAuthorizedCarrierData from "../../../models/payment-request/PaymentRequestAuthorizedCarrierData";
import PaymentRequestApiClient from "../../../api/PaymentRequestApiClient";
import {
  nameof,
  getTooltipForDecimalInput,
  getCurrencyDisplayName,
} from "../../../utilities/utilities";
import { numberFormat, currencyFormat } from "../../../utilities/utilities";
import { InfoOutlined } from "@material-ui/icons";
import Currency from "../../../models/shared/Currency";

interface Props extends StepHandler {
  authorizedCarriers: PaymentRequestAuthorizedCarrier[];
  authorizedCarriersDataState: PaymentRequestAuthorizedCarrierData[];
  schoolYearId: number;
  currency: Currency;
  updateAuthorizedCarriersData: (
    authorizedCarriersData: PaymentRequestAuthorizedCarrierData[]
  ) => void;
}

export default function PaymentRequestAuthorizedCarrierDataStep({
  authorizedCarriers,
  authorizedCarriersDataState,
  schoolYearId,
  currency,
  updateAuthorizedCarriersData,
  buttons,
  onPrevious,
  onNext,
  goToPreviousStep,
  goToNextStep,
  onStepperSave,
}: Props) {
  const classes = useStyles({});

  const [authorizedCarriersData, setAuthorizedCarriersData] = useState<
    PaymentRequestAuthorizedCarrierData[]
  >([]);

  useEffect(() => {
    if (authorizedCarriersDataState.length === 0) {
      let newAuthorizedCarriersData: PaymentRequestAuthorizedCarrierData[] = [];
      authorizedCarriers.forEach((ac) => {
        if (ac.isSelected) {
          newAuthorizedCarriersData.push({
            authorizedCarrier: ac,
          });
        }
      });

      setAuthorizedCarriersData(newAuthorizedCarriersData);
    } else {
      setAuthorizedCarriersData(authorizedCarriersDataState);
    }
  }, []);

  useEffect(() => {
    return updateTrackerDebounceEffect([
      [TrackerName.AuthorizedCarrierData, authorizedCarriersData],
    ]);
  }, [authorizedCarriersData]);

  const { handleNumberChangeArray, handleTextChangeArray } = useInput();
  const {
    isOpened: isSnackbarOpened,
    open: openSnackbar,
    message: snackbarMessage,
    close: closeSnackbar,
    variant: snackbarVariant,
  } = useSnackbar();

  const validationConfig = {
    trackerName: TrackerName.AuthorizedCarrierData,
    validationScheme: Yup.array().of(
      Yup.object<PaymentRequestAuthorizedCarrierData[]>()
        .shape({
          numberOfDays: Yup.number()
            .nullable()
            .min(0, "Broj dana mora biti između 0-24.")
            .max(24, "Broj dana mora biti između 0-24.")
            .required(),
        })
        .test("AuthorizedCarrierData", "Greška na prijevozu.", function (
          value: PaymentRequestAuthorizedCarrierData
        ) {
          const { path, createError, parent } = this;

          if (
            value.transportCost &&
            value.transportCostCorrected != null &&
            value.transportCost <= value.transportCostCorrected
          ) {
            return createError({
              path: path,
              message:
                "Korigirana cijena treba biti manja od početno izračunate cijene.",
            });
          }

          if (
            value.transportCostCorrected != null &&
            !isNaN(value.transportCostCorrected) &&
            (value.remark == null || value.remark == "" || value.remark == " ")
          ) {
            return createError({
              path: path,
              message:
                "Uz korigiranu cijenu potrebno je unijeti i napomenu kako biste nastavili na sljedeći korak zahtjeva.",
            });
          }

          return true;
        })
    ),
  } as ValidationConfiguration;

  const {
    validationResult,
    updateTrackerDebounceEffect,
    showErrors,
  } = useChangeTracker([validationConfig], false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const { type } = e.target;
    if (type === "number")
      handleNumberChangeArray(e, index, setAuthorizedCarriersData);
    else handleTextChangeArray(e, index, setAuthorizedCarriersData);
    e.target.focus();
  };

  const handleCostCorrectedBlur = (e: any, index: number) => {
    const { value, type } = e.target;
    const updatedCarriers = authorizedCarriersData.map((carrier, i) => {
      if (i === index) {
        if (
          value == null ||
          value == "" ||
          (type === "number" && isNaN(+value))
        ) {
          return {
            ...carrier,
            remark: undefined,
          };
        }
      }

      return carrier;
    });

    setAuthorizedCarriersData(updatedCarriers);
  };

  const handleCalculateCost = async (index: number) => {
    const authorizedCarrier = authorizedCarriersData[index].authorizedCarrier;
    const numberOfDays = authorizedCarriersData[index].numberOfDays;

    const apiClient = new PaymentRequestApiClient();
    const authorizedCarrierCost = await apiClient.calculateAuthorizedCarrierCost(
      schoolYearId,
      authorizedCarrier.id,
      numberOfDays || 0
    );

    setAuthorizedCarriersData((previousState) =>
      previousState.map((ps) => {
        if (ps.authorizedCarrier.id === authorizedCarrier.id) {
          return {
            ...ps,
            transportCost: authorizedCarrierCost.transportCost,
          };
        } else {
          return ps;
        }
      })
    );
  };

  const handleNextStep = () => {
    if (!validationResult.isValid(false)) {
      showErrors();

      const validationMessages = validationResult
        .getTrackerMessages(TrackerName.AuthorizedCarrierData, false)
        .filter(
          (m) =>
            m.validationType === "min" ||
            m.validationType === "max" ||
            m.validationType === "AuthorizedCarrierData"
        );

      let message = "";
      if (validationMessages.length > 0) {
        message = validationMessages[0].message;
      } else {
        message =
          "Ne možete prijeći na sljedeći korak dok ne ispunite sva obavezna polja. Molimo provjerite vrijednosti u crveno označenim poljima.";
      }

      openSnackbar(message, "error");
    } else {
      updateAuthorizedCarriersData(authorizedCarriersData);
      goToNextStep && goToNextStep();
    }
  };

  const handlePreviousStep = () => {
    if (goToPreviousStep != null) {
      updateAuthorizedCarriersData(authorizedCarriersData);
      goToPreviousStep();
    }
  };

  const handleUpdateState = () => {
    updateAuthorizedCarriersData(authorizedCarriersData);
  };

  onPrevious && onPrevious(handlePreviousStep);
  onNext && onNext(handleNextStep);
  onStepperSave && onStepperSave(handleUpdateState);

  return (
    <>
      {authorizedCarriersData.map((authorizedCarrierData, index) => (
        <ExpansionPanel key={authorizedCarrierData.authorizedCarrier.id}>
          <ExpansionPanelSummary
            expandIcon={<ExpandMoreIcon />}
            className={clsx(
              !validationResult.isArrayItemValid(
                TrackerName.AuthorizedCarrierData,
                index.toString()
              ) && classes.error
            )}
          >
            <Grid container spacing={4}>
              <Grid item md={2} lg={2}>
                <Typography variant="body2" color="textSecondary">
                  Naziv prijevoznika
                </Typography>
                <Typography variant="subtitle2">
                  {authorizedCarrierData.authorizedCarrier.name}
                </Typography>
              </Grid>
              <Grid item md={3} lg={2}>
                <Typography variant="body2" color="textSecondary">
                  Ukupna dnevna kilometraža
                </Typography>
                <Typography variant="subtitle2">
                  {numberFormat(
                    authorizedCarrierData.authorizedCarrier.dailyMileage,
                    "km",
                    4
                  )}
                </Typography>
              </Grid>
              <Grid item md={3} lg={2}>
                <Typography variant="body2" color="textSecondary">
                  Cijena po danu
                </Typography>
                <Typography variant="subtitle2">
                  {currencyFormat(
                    authorizedCarrierData.authorizedCarrier.dailyPrice, currency
                  )}
                </Typography>
              </Grid>
              <Grid item md={2} lg={1}>
                <Typography variant="body2" color="textSecondary">
                  Broj učenika
                </Typography>
                <Typography variant="subtitle2">
                  {authorizedCarrierData.authorizedCarrier.numberOfStudents}
                </Typography>
              </Grid>
              <Grid item md={2} lg={1}>
                <Typography variant="body2" color="textSecondary">
                  Broj dana
                </Typography>
                <Typography variant="subtitle2">
                  {authorizedCarrierData.numberOfDays
                    ? authorizedCarrierData.numberOfDays
                    : ""}
                </Typography>
              </Grid>
              <Grid item md={2} lg={2}>
                <Typography variant="body2" color="textSecondary">
                  Cijena
                </Typography>
                <Typography variant="subtitle2">
                  {authorizedCarrierData.transportCostCorrected != null &&
                  !isNaN(authorizedCarrierData.transportCostCorrected)
                    ? `${currencyFormat(
                        authorizedCarrierData.transportCost || 0, currency
                      )}  (${currencyFormat(
                        authorizedCarrierData.transportCostCorrected || 0, currency
                      )})`
                    : currencyFormat(authorizedCarrierData.transportCost || 0, currency)}
                </Typography>
              </Grid>
              <Grid item md={2} lg={2} className={classes.largeColumn}>
                <Typography variant="body2" color="textSecondary">
                  Napomena
                </Typography>
                <Typography variant="subtitle2">
                  {authorizedCarrierData.remark}
                </Typography>
              </Grid>
            </Grid>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails>
            <Grid container spacing={4}>
              <Grid item sm={12} md={6} lg={3}>
                <TrTextField
                  required
                  label="Broj dana"
                  name={nameof<typeof authorizedCarrierData>("numberOfDays")}
                  value={
                    authorizedCarrierData.numberOfDays != null
                      ? authorizedCarrierData.numberOfDays
                      : ""
                  }
                  onChange={(e) =>
                    handleChange(e as ChangeEvent<HTMLInputElement>, index)
                  }
                  onBlur={(e) => handleCalculateCost(index)}
                  type="number"
                  fullWidth
                  error={
                    !validationResult.isArrayPropertyValid(
                      TrackerName.AuthorizedCarrierData,
                      index.toString(),
                      nameof<typeof authorizedCarrierData>("numberOfDays")
                    )
                  }
                />
              </Grid>
              <Grid
                item
                sm={12}
                md={12}
                lg={12}
                className={classes.gridRowForSubTitle}
              >
                <Typography variant="h6">Korekcija iznosa</Typography>
              </Grid>
              <Grid
                item
                sm={12}
                md={12}
                lg={12}
                className={classes.gridRowForInfo}
              >
                <Card className={classes.cardInfo}>
                  <CardContent>
                    <div className={classes.cardInfoContent}>
                      <div className={classes.cardInfoIcon}>
                        <InfoOutlined />
                      </div>
                      <div className={classes.cardInfoText}>
                        {" "}
                        Upisujete samo u slučaju ako je ukupni iznos mjesečnog
                        troška prijevoznika manji od gore izračunatoga. U tom
                        slučaju, u polje upišite ukupni mjesečni iznos troškova!
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item sm={12} md={6} lg={3}>
                <TrTextField
                  label="Cijena"
                  name={nameof<typeof authorizedCarrierData>(
                    "transportCostCorrected"
                  )}
                  value={
                    authorizedCarrierData.transportCostCorrected != null
                      ? authorizedCarrierData.transportCostCorrected
                      : ""
                  }
                  onChange={(e) =>
                    handleChange(e as ChangeEvent<HTMLInputElement>, index)
                  }
                  onBlur={(e) => handleCostCorrectedBlur(e, index)}
                  type="number"
                  tooltip={getTooltipForDecimalInput()}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{getCurrencyDisplayName(currency)}</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item sm={12} md={6} lg={3}>
                <TrTextField
                  disabled={
                    authorizedCarrierData.transportCostCorrected == null ||
                    isNaN(authorizedCarrierData.transportCostCorrected)
                  }
                  label="Napomena"
                  name={nameof<typeof authorizedCarrierData>("remark")}
                  className={
                    authorizedCarrierData.transportCostCorrected == null ||
                    isNaN(authorizedCarrierData.transportCostCorrected)
                      ? classes.disabledTextField
                      : ""
                  }
                  value={
                    authorizedCarrierData.remark != null
                      ? authorizedCarrierData.remark
                      : ""
                  }
                  onChange={(e) =>
                    handleChange(e as ChangeEvent<HTMLInputElement>, index)
                  }
                  fullWidth
                />
              </Grid>
            </Grid>
          </ExpansionPanelDetails>
        </ExpansionPanel>
      ))}
      {buttons}
      <TrSnackbar
        variant={snackbarVariant}
        isOpened={isSnackbarOpened}
        message={snackbarMessage}
        onClose={closeSnackbar}
      />
    </>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    error: {
      color: theme.palette.error.main,
    },
    largeColumn: {
      display: "flex",
      alignItems: "start",
      flexDirection: "column",
      flexGrow: 1.5,
      flexBasis: 0,
    },
    gridRowForSubTitle: {
      marginTop: theme.spacing(1),
    },
    gridRowForInfo: {
      marginTop: -theme.spacing(1),
    },
    cardInfo: {
      display: "inline-flex",
      flex: 1,
      background: theme.palette.primary.main.toString(),
      color: "white",
    },
    cardInfoContent: {
      display: "flex",
      alignItems: "center",
      marginBottom: "-8px",
    },
    cardInfoIcon: {
      display: "flex",
      marginRight: theme.spacing(1),
    },
    cardInfoText: {
      display: "flex",
    },
    disabledTextField: {
      opacity: 0.4,
    },
  })
);
